/**
 * The Apps registry: every app Amoreg offers, in one list.
 *
 * An app is either first-party (Contract Intelligence, built into Amoreg) or
 * an integration the workspace server catalogues (SharePoint, iManage,
 * Salesforce…). The registry says what the server does not: which category an
 * app belongs in, how its status is read, and which page opens it. Names,
 * descriptions and icons of integrations still come from the server, so the
 * catalogue and this list cannot disagree about what an integration is.
 *
 * Adding an app is one entry in APP_DEFINITIONS (and, for a new kind of page,
 * one entry in AppDetailPage's component map). An integration the server
 * offers that has no entry still appears, under "Other"; an entry whose
 * integration the server does not offer reads "Coming soon", never
 * "Connected".
 *
 * Pure: the page, the Apps sidebar and Home's sidebar all read the same
 * entries, and the tests pin them.
 *
 * @module components/apps/appRegistry
 */
import type { CustomMcpServer } from "../../state/customMcpServers";
import type { Integration } from "../../state/governance";
import { organizationCustomMcpServers } from "./integrations/customMcpServerContent";
import { sortIntegrations } from "./integrations/integrationContent";

export const APP_CATEGORIES = [
  { id: "amoreg", label: "Amoreg apps" },
  { id: "documents", label: "Document management" },
  { id: "work", label: "CRM and work" },
  { id: "compliance", label: "Compliance tools" },
  { id: "custom", label: "Custom" },
  { id: "other", label: "Other" },
] as const;
export type AppCategory = (typeof APP_CATEGORIES)[number]["id"];

export const isAppCategory = (value: unknown): value is AppCategory =>
  typeof value === "string" && APP_CATEGORIES.some((category) => category.id === value);

export const appCategoryLabel = (category: AppCategory): string =>
  APP_CATEGORIES.find((entry) => entry.id === category)?.label ?? "Other";

/**
 * Where an app's status comes from.
 * - `first-party`: always available; there is nothing to connect.
 * - `organization`: the organization's connection in the integrations catalogue.
 * - `personal`: the acting member's own connection (iManage), never the org's settings.
 */
export type AppStatusSource = "first-party" | "organization" | "personal";

/** Which page opens the app (AppDetailPage maps each to its component). */
export type AppDetailKind = "contract-intelligence" | "probe" | "integration" | "imanage";

/** The glyph beside a landing capability (AppLandingPage maps each to an icon). */
export type AppLandingIcon = "workspaces" | "preview" | "chat" | "review";

export interface AppLandingCapability {
  readonly icon: AppLandingIcon;
  readonly title: string;
  readonly body: string;
}

/**
 * The page an app opens on until the person connects it (appGate). Only what
 * the app really does in Amoreg: no screenshots, no claims about the vendor.
 */
export interface AppLanding {
  /** One line under the name: what the app is for, in Amoreg. */
  readonly tagline: string;
  /** Three or four things the person can do with it here. */
  readonly capabilities: ReadonlyArray<AppLandingCapability>;
}

export interface AppDefinition {
  readonly id: string;
  readonly name: string;
  readonly category: AppCategory;
  /** One line; an integration's own description from the server wins when it has one. */
  readonly description: string;
  readonly statusSource: AppStatusSource;
  readonly detail: AppDetailKind;
  /**
   * Set for an app area that needs the person's own connection: it opens on
   * this landing until connected (appGate), and on the app itself after.
   */
  readonly landing?: AppLanding;
}

export const CONTRACT_INTELLIGENCE_APP_ID = "contract-intelligence";
export const PROBE_APP_ID = "probe";

export const APP_DEFINITIONS: ReadonlyArray<AppDefinition> = [
  {
    id: CONTRACT_INTELLIGENCE_APP_ID,
    name: "Contract Intelligence",
    category: "amoreg",
    description: "Review contracts and agreements against your policies and regimes.",
    statusSource: "first-party",
    detail: "contract-intelligence",
  },
  {
    id: PROBE_APP_ID,
    name: "Probe",
    category: "amoreg",
    description: "Rehearse the regulator's interview by voice, grounded in your policies.",
    statusSource: "first-party",
    detail: "probe",
  },
  {
    id: "sharepoint",
    name: "SharePoint",
    category: "documents",
    description: "Find and read the firm's documents in SharePoint.",
    statusSource: "organization",
    detail: "integration",
  },
  {
    id: "googledrive",
    name: "Google Drive",
    category: "documents",
    description: "Find and read the firm's documents in Google Drive.",
    statusSource: "organization",
    detail: "integration",
  },
  {
    id: "onedrive",
    name: "OneDrive",
    category: "documents",
    description: "Find and read files in OneDrive.",
    statusSource: "organization",
    detail: "integration",
  },
  {
    id: "imanage",
    name: "iManage Cloud",
    category: "documents",
    description: "Review iManage documents with your own iManage access.",
    statusSource: "personal",
    detail: "imanage",
    landing: {
      tagline: "Your firm's iManage documents in Amoreg, with your own iManage access.",
      capabilities: [
        {
          icon: "workspaces",
          title: "Browse workspaces and matters",
          body: "Libraries, client and matter workspaces and their folders, with search, recent documents and favorites.",
        },
        {
          icon: "preview",
          title: "Preview documents and versions",
          body: "Read documents and filed emails in place, and move between their versions.",
        },
        {
          icon: "chat",
          title: "Ask Amoreg about a document",
          body: "Start a chat with the document attached, or pick iManage documents for a contract review.",
        },
        {
          icon: "review",
          title: "Review and save back",
          body: "Review a Word document's tracked changes and save the result to iManage once you confirm the exact file.",
        },
      ],
    },
  },
  {
    id: "laserfiche",
    name: "Laserfiche",
    category: "documents",
    description: "Search and read records held in Laserfiche.",
    statusSource: "organization",
    detail: "integration",
  },
  {
    id: "salesforce",
    name: "Salesforce",
    category: "work",
    description: "Read client and account records in Salesforce.",
    statusSource: "organization",
    detail: "integration",
  },
  {
    id: "hubspot",
    name: "HubSpot",
    category: "work",
    description: "Read contacts, companies and deals in HubSpot.",
    statusSource: "organization",
    detail: "integration",
  },
  {
    id: "altruist",
    name: "Altruist",
    category: "work",
    description: "Read accounts and households in Altruist.",
    statusSource: "organization",
    detail: "integration",
  },
  {
    id: "jira",
    name: "Jira",
    category: "work",
    description: "Find and track remediation work in Jira.",
    statusSource: "organization",
    detail: "integration",
  },
  {
    id: "ruleguard",
    name: "RuleGuard",
    category: "compliance",
    description: "Bring RuleGuard's compliance records into Amoreg.",
    statusSource: "organization",
    detail: "integration",
  },
  {
    id: "plainsail",
    name: "PlainSail",
    category: "compliance",
    description: "Search PlainSail for compliance updates and activity.",
    statusSource: "organization",
    detail: "integration",
  },
];

const DEFINITIONS_BY_ID = new Map(APP_DEFINITIONS.map((definition) => [definition.id, definition]));

export const appDefinition = (id: string): AppDefinition | null =>
  DEFINITIONS_BY_ID.get(id) ?? null;

/** The landing a connection-gated app opens on, or null for an app that needs no connection. */
export const appLanding = (id: string): AppLanding | null => appDefinition(id)?.landing ?? null;

/** Which page opens an app id; anything the registry does not know is a catalogue integration. */
export const appDetailKind = (id: string): AppDetailKind =>
  appDefinition(id)?.detail ?? "integration";

/** The status a row shows. Integrations keep their richer pill (integrationStatus) on their row. */
export type AppAvailability = "included" | "connected" | "not-connected" | "coming-soon";

export type AppEntry =
  | {
      readonly kind: "first-party";
      readonly id: string;
      readonly name: string;
      readonly description: string;
      readonly category: AppCategory;
      readonly availability: "included";
    }
  | {
      readonly kind: "integration";
      readonly id: string;
      readonly name: string;
      readonly description: string;
      readonly category: AppCategory;
      readonly availability: "connected" | "not-connected";
      readonly integration: Integration;
    }
  | {
      readonly kind: "custom";
      readonly id: string;
      readonly name: string;
      readonly description: string;
      readonly category: "custom";
      readonly availability: "connected" | "not-connected";
      readonly server: CustomMcpServer;
    }
  | {
      readonly kind: "coming-soon";
      readonly id: string;
      readonly name: string;
      readonly description: string;
      readonly category: AppCategory;
      readonly availability: "coming-soon";
    };

/** iManage shows the acting member's own connection, never the organization's settings. */
export interface PersonalConnection {
  readonly connected: boolean;
  readonly configured: boolean;
  readonly pending: boolean;
}

export function withPersonalConnection(
  integration: Integration,
  personal: PersonalConnection | null,
): Integration {
  return {
    ...integration,
    connected: personal?.connected === true,
    configured: personal?.configured === true,
    connectionStatus: personal?.connected
      ? "connected"
      : personal?.pending
        ? "pending"
        : "disconnected",
  };
}

export interface AppSources {
  /** The organization's integrations catalogue; null while it loads. */
  readonly integrations: ReadonlyArray<Integration> | null;
  /** The member's iManage connection; null when unknown. */
  readonly personal: PersonalConnection | null;
  /** Custom MCP servers; only the organization's are apps (personal ones live under Plugins). */
  readonly customServers: ReadonlyArray<CustomMcpServer> | null;
}

/**
 * Every app for the organization, in registry order within each category:
 * first-party apps, then registered integrations, then anything else the
 * server offers, then the organization's custom MCP servers.
 */
export function buildAppEntries(sources: AppSources): ReadonlyArray<AppEntry> {
  const catalogue = new Map(
    (sources.integrations ?? []).map((integration) => [integration.id, integration]),
  );
  const entries: AppEntry[] = [];
  for (const definition of APP_DEFINITIONS) {
    if (definition.statusSource === "first-party") {
      entries.push({
        kind: "first-party",
        id: definition.id,
        name: definition.name,
        description: definition.description,
        category: definition.category,
        availability: "included",
      });
      continue;
    }
    const offered = catalogue.get(definition.id);
    if (!offered) {
      // Only once the catalogue has answered: while it loads, nothing is "coming soon".
      if (sources.integrations !== null) {
        entries.push({
          kind: "coming-soon",
          id: definition.id,
          name: definition.name,
          description: definition.description,
          category: definition.category,
          availability: "coming-soon",
        });
      }
      continue;
    }
    const integration =
      definition.statusSource === "personal"
        ? withPersonalConnection(offered, sources.personal)
        : offered;
    entries.push({
      kind: "integration",
      id: integration.id,
      name: integration.name,
      description: integration.description.trim() || definition.description,
      category: definition.category,
      availability: integration.connected ? "connected" : "not-connected",
      integration,
    });
  }
  const unregistered = sortIntegrations(
    (sources.integrations ?? []).filter((integration) => !DEFINITIONS_BY_ID.has(integration.id)),
  );
  for (const integration of unregistered) {
    entries.push({
      kind: "integration",
      id: integration.id,
      name: integration.name,
      description: integration.description,
      category: "other",
      availability: integration.connected ? "connected" : "not-connected",
      integration,
    });
  }
  for (const server of organizationCustomMcpServers(sources.customServers ?? [])) {
    entries.push({
      kind: "custom",
      id: server.id,
      name: server.name,
      description: "Custom MCP server",
      category: "custom",
      availability: server.enabled ? "connected" : "not-connected",
      server,
    });
  }
  return entries;
}

/** Entries matching a category (null for all) and a search, connected first within each category. */
export function filterAppEntries(
  entries: ReadonlyArray<AppEntry>,
  options: { readonly category: AppCategory | null; readonly query: string },
): ReadonlyArray<AppEntry> {
  const query = options.query.trim().toLowerCase();
  return entries.filter(
    (entry) =>
      (options.category === null || entry.category === options.category) &&
      (query.length === 0 ||
        entry.name.toLowerCase().includes(query) ||
        entry.description.toLowerCase().includes(query) ||
        appCategoryLabel(entry.category).toLowerCase().includes(query)),
  );
}

/** Entries grouped by category, in the registry's category order, empty categories left out. */
export function groupAppEntries(
  entries: ReadonlyArray<AppEntry>,
): ReadonlyArray<{
  readonly category: AppCategory;
  readonly label: string;
  readonly entries: ReadonlyArray<AppEntry>;
}> {
  return APP_CATEGORIES.flatMap((category) => {
    const inCategory = entries.filter((entry) => entry.category === category.id);
    return inCategory.length === 0
      ? []
      : [{ category: category.id, label: category.label, entries: inCategory }];
  });
}

/** How many apps each category holds, for the Apps sidebar. */
export function appCategoryCounts(
  entries: ReadonlyArray<AppEntry>,
): ReadonlyMap<AppCategory, number> {
  const counts = new Map<AppCategory, number>();
  for (const entry of entries) counts.set(entry.category, (counts.get(entry.category) ?? 0) + 1);
  return counts;
}

/** At most this many connected apps sit in Home's sidebar. */
export const HOME_CONNECTED_APP_LIMIT = 4;

/**
 * Home's Apps group: the member's connected apps (at most four, by name),
 * then the first-party apps. "All apps" follows them in the sidebar itself.
 */
export function homeSidebarApps(entries: ReadonlyArray<AppEntry>): ReadonlyArray<AppEntry> {
  const connected = entries
    .filter((entry) => entry.availability === "connected")
    .toSorted((a, b) => a.name.localeCompare(b.name))
    .slice(0, HOME_CONNECTED_APP_LIMIT);
  const firstParty = entries.filter((entry) => entry.kind === "first-party");
  return [...connected, ...firstParty];
}
